import parse from "html-react-parser";
import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  useForm,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import {
  MdOutlineSentimentDissatisfied,
  MdOutlineSentimentNeutral,
  MdOutlineSentimentSatisfied,
  MdOutlineSentimentVeryDissatisfied,
  MdOutlineSentimentVerySatisfied,
  MdStar,
  MdThumbDown,
  MdThumbUp,
} from "react-icons/md";
import Select from "react-select";
import { Rating } from "react-simple-star-rating";
import { CustomIcon } from "../../utils/common";
import { customIconsNPS } from "./NPSIconDiv";
import "./RecursiveInnerDetailSurveyForm.scss";
import { DetailedInfo } from "./types";

interface ChildProps {
  RecursiveInnerSurveySections: DetailedInfo[] | null;
  register: UseFormRegister<any>;
  control: Control<any>;
  errors: FieldErrors<any>;
  watch: UseFormWatch<any>;
  IsinnerSubSection: boolean | false;
  ParentQuestionId?: number | null;
  showSubmitButton: any;
  setValue: UseFormSetValue<any>;
  questionCounter: number;
  isDraft: boolean;
  submitButtonClicked: number;
}

interface MatrixField {
  FieldId?: number;
  FieldName: string;
  IsActive: boolean;
  QuestionId: number;
  value: string;
  OrganizationId: number;
}

interface MatrixForm {
  fields: MatrixField[];
}

const RecursiveInnerDetailSurveyForm: React.FC<ChildProps> = ({
  register,
  control,
  errors,
  RecursiveInnerSurveySections,
  IsinnerSubSection,
  watch,
  ParentQuestionId,
  showSubmitButton,
  setValue,
  questionCounter,
  isDraft,
  submitButtonClicked,
}) => {
  const [applyValidation, setApplyValidation] = useState(true);
  const formRef = useRef<HTMLDivElement>(null);

  const matrixMethods = useForm<MatrixForm>();

  const {
    fields: matrixFields,
    append: appendMatrixField,
    remove: removeMatrixField,
    replace,
  } = useFieldArray<MatrixForm, "fields">({
    control: matrixMethods.control,
    name: "fields",
  });

  useEffect(() => {
    const firstErrorField = Object.keys(errors)?.[0];
    if (firstErrorField) {
      setTimeout(() => {
        let element =
          formRef.current?.querySelector(`[data-name="${firstErrorField}"]`) ??
          formRef.current?.querySelector(`[name="${firstErrorField}"]`);

        const focusTarget =
          element?.querySelector?.("input,select,textarea") ?? element;

        if (element && focusTarget instanceof HTMLElement) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          focusTarget.focus();
        }
      }, 500);
    }
  }, [errors, submitButtonClicked]);

  const getErrorMessage = (questionId: number) => {
    const error = errors[`question_${questionId}`];
    if (error) {
      return (error.message as string) || "";
    }
    return "";
  };

  let questionIds: any[] = [];

  const handleInputChange = (
    questionId: number,
    responseId: any,
    fieldId?: number
  ) => {
    responseId = parseInt(responseId, 10);

    if (!questionIds.includes(questionId)) {
      questionIds.push(questionId);
    }

    let listOfResponseIdNotSelected: any = [];
    RecursiveInnerSurveySections?.forEach((section) => {
      if (section?.QuestionId === questionId) {
        section?.SurveyQuestionResponseDetails?.forEach((responseDetail) => {
          if (responseDetail?.QuestionResponseOptions) {
            responseDetail?.QuestionResponseOptions?.forEach((option) => {
              if (option?.QuestionResponseOptionId !== responseId) {
                listOfResponseIdNotSelected?.push({
                  QuestionResponseOptionId: option?.QuestionResponseOptionId,
                  QuestionId: section?.QuestionId,
                  FieldId: fieldId, // Include fieldId in the not selected options
                });
              }
            });
          }
        });
      }
    });

    // Gather selected values for submission
    const matrixSelections = fieldId && {
      fieldId,
      responseId,
      questionId,
    };

    let matchingQuestions: any = [];
    listOfResponseIdNotSelected?.forEach((item: any) => {
      RecursiveInnerSurveySections?.forEach((section: any) => {
        if (
          section?.QuestionResponseOptionId === item?.QuestionResponseOptionId
        ) {
          matchingQuestions?.push({
            QuestionResponseOptionId: item?.QuestionResponseOptionId,
            QuestionId: section?.QuestionId,
            FieldId: item?.FieldId,
          });

          if (item?.FieldId) {
            handleMatrixInputChange(
              item?.FieldId,
              item?.QuestionResponseOptionId,
              null
            );
          }

          setValue(`question_${section?.QuestionId}`, null);

          handleInputChange(
            section?.QuestionId,
            item?.QuestionResponseOptionId,
            item?.FieldId
          );
        }
      });
    });

    return matrixSelections;
  };
  const handleMatrixInputChange = (
    fieldId: number,
    optionId: number,
    value: any
  ) => {
    const matrixSelection = {
      fieldId,
      optionId,
      value,
    };
  };

  const customIcons = [
    {
      icon: <MdOutlineSentimentVeryDissatisfied size={50} />,
      fillColor: "#ef1324",
    },
    {
      icon: <MdOutlineSentimentDissatisfied size={50} />,
      fillColor: "#f78411",
    },
    { icon: <MdOutlineSentimentNeutral size={50} />, fillColor: "#f7e017" },
    { icon: <MdOutlineSentimentSatisfied size={50} />, fillColor: "#73ca31" },
    {
      icon: <MdOutlineSentimentVerySatisfied size={50} />,
      fillColor: "#38af48",
    },
  ];

  const ThumsIcons = [
    { icon: <MdThumbDown size={35} />, fillColor: "#ef1324" },
    { icon: <MdThumbUp size={35} />, fillColor: "#38af48" },
  ];

  useEffect(() => {
    setApplyValidation(showSubmitButton);
  }, [showSubmitButton]);

  return (
    <>
      <div ref={formRef}>
        {RecursiveInnerSurveySections &&
        RecursiveInnerSurveySections?.length > 0 ? (
          RecursiveInnerSurveySections?.map((question) => (
            <div>
              {(() => {
                const parentVal = question?.BranchQuestionId
                  ? watch(`question_${question?.BranchQuestionId}`)
                  : null;
                const isStarBranch = (question as any)?.IsStarBranch;
                const optionId = question?.QuestionResponseOptionId;
                
                // No branch parent → always visible
                if (!question?.BranchQuestionId) return true;
                if (!parentVal) return false;
                
                if (isStarBranch && optionId) {
                  // Star branch: optionId is comma-separated star values like "1,2"
                  const triggerValues = optionId.toString().split(",");
                  return triggerValues.includes(parentVal?.toString());
                }
                
                // Normal branch: exact match on QuestionResponseOptionId
                return parentVal == optionId;
              })() && (
                <div
                  className="questions "
                  key={question?.QuestionId}
                  data-name={`question_${question?.QuestionId}`}
                >
                  <h5>
                    <div className="d-flex align-items-start gap-2">
                      <div>{questionCounter++}.</div>
                      {question?.IsHtml ? (
                        <>
                          <div className="quill-content">
                            {parse(question?.QuestionTitleHtml || "")}
                          </div>
                          {question?.IsRequired === true && (
                            <span className="text-danger ml-1">*</span>
                          )}
                        </>
                      ) : (
                        <div>
                          {question?.QuestionTitle}
                          {question?.IsRequired === true && (
                            <span className="text-danger ml-1">*</span>
                          )}
                        </div>
                      )}
                    </div>
                  </h5>
                  {question?.Instruction && (
                    <div className="ques-instruction mb-3 mt-2">
                      {question?.Instruction}
                    </div>
                  )}
                  {question?.SurveyQuestionResponseDetails?.map(
                    (responseDetail) => (
                      <div key={responseDetail?.QuestionResponseId}>
                        {responseDetail?.ResponseTypeName === "DropDown" && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            render={({ field: { onChange, value } }) => (
                              <>
                                <Form.Control
                                  as="select"
                                  value={value || ""}
                                  onChange={(e) => {
                                    onChange(e);
                                    handleInputChange(
                                      question?.QuestionId,
                                      e.target.value
                                    );
                                  }}
                                  disabled={!applyValidation && !isDraft}
                                >
                                  <option value="">Select an option</option>
                                  {responseDetail?.QuestionResponseOptions?.map(
                                    (option) => (
                                      <option
                                        key={option?.QuestionResponseOptionId}
                                        value={option?.QuestionResponseOptionId}
                                      >
                                        {option?.QuestionResponseOptionValue}
                                      </option>
                                    )
                                  )}
                                </Form.Control>
                                {errors[`question_${question?.QuestionId}`] && (
                                  <span className="text-danger">
                                    {getErrorMessage(question?.QuestionId)}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        )}

                        {responseDetail?.ResponseTypeName ===
                          "Priority Ranking" && (
                          <div className="table-responsive">
                            {(() => {
                              const selectedRanks = watch()
                                ? Object.entries(watch())
                                    ?.filter(([key]) =>
                                      key.startsWith("priority_")
                                    )
                                    ?.map(([_, val]) => val)
                                : [];
                              return (
                                <table className="table table-bordered">
                                  <thead>
                                    <tr>
                                      <th>Fields</th>
                                      <th>Rank</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {question?.MatrixFieldDetails?.map(
                                      (field) => (
                                        <tr key={field?.FieldId}>
                                          <td>{field?.FieldName}</td>
                                          <td
                                            data-name={`priority_${field?.FieldId}`}
                                          >
                                            <Controller
                                              name={`priority_${field?.FieldId}`}
                                              control={control}
                                              rules={{
                                                required:
                                                  applyValidation &&
                                                  question?.IsRequired
                                                    ? "This field is required"
                                                    : false,
                                              }}
                                              render={({
                                                field: { onChange, value },
                                                fieldState: { error },
                                              }) => (
                                                <>
                                                  <Form.Control
                                                    as="select"
                                                    value={value || ""}
                                                    onChange={(e) => {
                                                      const selectedValue =
                                                        e.target.value;
                                                      onChange(selectedValue);
                                                      handleInputChange(
                                                        question?.QuestionId,
                                                        selectedValue,
                                                        field?.FieldId
                                                      );
                                                    }}
                                                    disabled={
                                                      !applyValidation &&
                                                      !isDraft
                                                    }
                                                  >
                                                    <option value="">
                                                      Select a rank
                                                    </option>
                                                    {responseDetail?.QuestionResponseOptions?.map(
                                                      (option) => {
                                                        const isDisabled =
                                                          selectedRanks?.includes(
                                                            option?.QuestionResponseOptionId?.toString()
                                                          ) &&
                                                          value !==
                                                            option?.QuestionResponseOptionId?.toString();

                                                        return (
                                                          <option
                                                            key={
                                                              option?.QuestionResponseOptionId
                                                            }
                                                            value={
                                                              option?.QuestionResponseOptionId
                                                            }
                                                            disabled={
                                                              isDisabled
                                                            }
                                                          >
                                                            {
                                                              option?.QuestionResponseOptionValue
                                                            }
                                                          </option>
                                                        );
                                                      }
                                                    )}
                                                  </Form.Control>
                                                  {error && (
                                                    <span className="text-danger">
                                                      {error.message}
                                                    </span>
                                                  )}
                                                </>
                                              )}
                                            />
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              );
                            })()}
                          </div>
                        )}

                        {responseDetail?.ResponseTypeName ===
                          "MatrixTypeResponse" && (
                          <div className="table-responsive">
                            <table className="table table-bordered">
                              <thead>
                                <tr>
                                  <th>Fields</th>
                                  {responseDetail?.QuestionResponseOptions?.map(
                                    (option) => (
                                      <th
                                        key={option?.QuestionResponseOptionId}
                                     className="text-center" style={{ width: "90px" }}>
                                        {option?.QuestionResponseOptionValue}
                                      </th>
                                    )
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {question?.MatrixFieldDetails?.map(
                                  (field, fieldIndex) => (
                                    <tr key={field?.FieldId}>
                                      <td>{field?.FieldName}</td>
                                      {responseDetail?.QuestionResponseOptions?.map(
                                        (option) => (
                                          <td
                                            key={
                                              option?.QuestionResponseOptionId
                                            }
                                            data-name={`matrix_${field?.FieldId}`}
                                          >
                                            <Controller
                                              name={`matrix_${field?.FieldId}`}
                                              control={control}
                                              rules={{
                                                required:
                                                  applyValidation &&
                                                  question?.IsRequired === true
                                                    ? "This field is required"
                                                    : false,
                                              }}
                                              render={({
                                                field: { onChange, value },
                                                fieldState: { error },
                                              }) => (
                                                <>
                                                  <Form.Check
                                                    type="radio"
                                                    name={`matrix_${field?.FieldId}`}
                                                    value={
                                                      option?.QuestionResponseOptionId
                                                    }
                                                    checked={
                                                      value ==
                                                      option?.QuestionResponseOptionId
                                                    }
                                                    onChange={(e) => {
                                                      onChange(e);
                                                      handleMatrixInputChange(
                                                        field?.FieldId ?? 0,
                                                        option?.QuestionResponseOptionId ??
                                                          0,
                                                        e.target.value
                                                      );
                                                      handleInputChange(
                                                        question?.QuestionId,
                                                        e.target.value,
                                                        field?.FieldId
                                                      );
                                                    }}
                                                    disabled={
                                                      !applyValidation &&
                                                      !isDraft
                                                    }
                                                  />
                                                  {error && (
                                                    <span className="text-danger">
                                                      {error.message}
                                                    </span>
                                                  )}
                                                </>
                                              )}
                                            />
                                          </td>
                                        )
                                      )}
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {(responseDetail?.ResponseTypeName === "Radio Button" ||
                          responseDetail?.ResponseTypeName === "Yes or No") && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            render={({ field: { onChange, value } }) => (
                              <>
                                <div className="flex-wrap d-flex">
                                  {responseDetail?.QuestionResponseOptions?.map(
                                    (option) => (
                                      <Form.Check
                                        key={option?.QuestionResponseOptionId}
                                        type="radio"
                                        label={
                                          option?.QuestionResponseOptionValue
                                        }
                                        value={option?.QuestionResponseOptionId}
                                        checked={
                                          value ==
                                          option?.QuestionResponseOptionId
                                        }
                                        onChange={(e) => {
                                          onChange(e);
                                          handleInputChange(
                                            question?.QuestionId,
                                            e.target.value
                                          );
                                        }}
                                        disabled={!applyValidation && !isDraft}
                                      />
                                    )
                                  )}
                                </div>
                                {errors[`question_${question?.QuestionId}`] && (
                                  <span className="text-danger">
                                    {getErrorMessage(question?.QuestionId)}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        )}

                        {responseDetail?.ResponseTypeName ===
                          "Multi-Select DropDown" && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            render={({ field: { onChange, value = [] } }) => (
                              <>
                                <Select
                                  isMulti
                                  value={(Array.isArray(value)
                                    ? value
                                    : [value]
                                  )
                                    ?.map((selectedValue) => {
                                      const matchedOption =
                                        responseDetail?.QuestionResponseOptions?.find(
                                          (option) =>
                                            option?.QuestionResponseOptionId?.toString() ===
                                            selectedValue?.toString()
                                        );
                                      return matchedOption
                                        ? {
                                            value:
                                              matchedOption?.QuestionResponseOptionId,
                                            label:
                                              matchedOption?.QuestionResponseOptionValue,
                                          }
                                        : null;
                                    })
                                    ?.filter(Boolean)}
                                  onChange={(selectedOptions) => {
                                    const selectedValues = selectedOptions
                                      ? selectedOptions?.map(
                                          (option) => option?.value
                                        )
                                      : [];
                                    onChange(selectedValues);
                                  }}
                                  options={responseDetail?.QuestionResponseOptions?.map(
                                    (option) => ({
                                      value: option?.QuestionResponseOptionId,
                                      label:
                                        option?.QuestionResponseOptionValue,
                                    })
                                  )}
                                  isDisabled={!applyValidation && !isDraft}
                                  classNamePrefix="react-select"
                                  placeholder="Select options"
                                  onMenuOpen={() => {
                                    console.log("Menu opened");
                                  }}
                                  onMenuClose={() => {
                                    console.log("Menu closed");
                                  }}
                                  onInputChange={(newValue, actionMeta) => {
                                    console.log(
                                      `Input changed to: ${newValue}`,
                                      actionMeta
                                    );
                                  }}
                                />

                                {errors[`question_${question?.QuestionId}`] && (
                                  <span className="text-danger">
                                    {getErrorMessage(question?.QuestionId)}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        )}

                        {responseDetail?.ResponseTypeName === "CheckBox" && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            defaultValue={[]}
                            render={({ field: { onChange, value = [] } }) => {
                              const checkedValues =
                                typeof value === "string"
                                  ? value.split(",")
                                  : Array.isArray(value)
                                  ? value
                                  : [];

                              return (
                                <>
                                  <div className="flex-wrap d-flex">
                                    {responseDetail?.QuestionResponseOptions?.map(
                                      (option) => {
                                        const optionId =
                                          option?.QuestionResponseOptionId.toString();
                                        const isChecked =
                                          checkedValues?.includes(optionId);

                                        return (
                                          <Form.Check
                                            key={optionId}
                                            type="checkbox"
                                            label={
                                              option?.QuestionResponseOptionValue
                                            }
                                            value={optionId}
                                            checked={isChecked}
                                            onChange={() => {
                                              let newValue = Array.isArray(
                                                checkedValues
                                              )
                                                ? [...checkedValues]
                                                : [];

                                              if (
                                                newValue?.includes(optionId)
                                              ) {
                                                newValue = newValue?.filter(
                                                  (v) => v !== optionId
                                                );
                                              } else {
                                                newValue?.push(optionId);
                                              }

                                              onChange(newValue);
                                              handleInputChange(
                                                question?.QuestionId,
                                                newValue
                                              );
                                            }}
                                            disabled={
                                              !applyValidation && !isDraft
                                            }
                                          />
                                        );
                                      }
                                    )}
                                  </div>
                                  {errors[
                                    `question_${question?.QuestionId}`
                                  ] && (
                                    <span className="text-danger">
                                      {getErrorMessage(question?.QuestionId)}
                                    </span>
                                  )}
                                </>
                              );
                            }}
                          />
                        )}

                        {responseDetail?.ResponseTypeName === "TextBox" && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                              validate: (value) => {
                                if (
                                  applyValidation &&
                                  question?.IsRequired === true
                                ) {
                                  return (
                                    value?.trim() !== "" ||
                                    "This field cannot contain only spaces"
                                  );
                                }
                                return true;
                              },
                            }}
                            render={({ field: { onChange, value } }) => (
                              <>
                                <Form.Control
                                  type="text"
                                  value={value || ""}
                                  onChange={onChange}
                                  disabled={!applyValidation && !isDraft}
                                />
                                {errors[`question_${question?.QuestionId}`] && (
                                  <span className="text-danger">
                                    {getErrorMessage(question?.QuestionId)}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        )}

                        {responseDetail?.ResponseTypeName === "TextArea" && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            render={({ field: { onChange, value } }) => (
                              <>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  value={value || ""}
                                  onChange={onChange}
                                />
                                {errors[`question_${question?.QuestionId}`] && (
                                  <span className="text-danger">
                                    {getErrorMessage(question?.QuestionId)}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        )}

                        {responseDetail?.ResponseTypeName === "DatePicker" && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            render={({ field: { onChange, value } }) => (
                              <>
                                <Form.Control
                                  type="date"
                                  value={value || ""}
                                  onChange={onChange}
                                />
                                {errors[`question_${question?.QuestionId}`] && (
                                  <span className="text-danger">
                                    {getErrorMessage(question?.QuestionId)}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        )}

                        {responseDetail?.ResponseTypeName === "Smiley" && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            render={({ field: { onChange, value } }) => (
                              <>
                                <Rating
                                  onClick={(ratingValue) => {
                                    onChange(ratingValue);
                                  }}
                                  size={25}
                                  readonly={!applyValidation && !isDraft}
                                  initialValue={value}
                                  customIcons={customIcons?.map(
                                    (iconObj, index) => ({
                                      icon: (
                                        <span
                                          aria-label={`Smiley ${index + 1}`}
                                          key={index}
                                          style={{
                                            margin: "3px",
                                            color:
                                              value == index + 1
                                                ? iconObj.fillColor
                                                : "#ccc",
                                            transition: "color 0.9s",
                                          }}
                                        >
                                          {iconObj.icon}
                                        </span>
                                      ),
                                    })
                                  )}
                                  iconsCount={5}
                                  disableFillHover={true}
                                />
                                <br />
                                {errors[`question_${question?.QuestionId}`] && (
                                  <span className="text-danger">
                                    {getErrorMessage(question?.QuestionId)}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        )}

                        {responseDetail?.ResponseTypeName === "NPS" && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            render={({ field: { onChange, value } }) => {
                              const isReadOnly = !applyValidation && !isDraft;
                              return (
                                <>
                                  <div className="nps-rating-blk">
                                    <Rating
                                      onClick={(value) => {
                                        onChange(value);
                                      }}
                                      size={25}
                                      readonly={isReadOnly}
                                      initialValue={value}
                                      customIcons={customIconsNPS?.map(
                                        (
                                          iconObj: CustomIcon,
                                          index: number
                                        ) => ({
                                          icon: (
                                            <span
                                              key={index}
                                              aria-label={`NPS ${index + 1}`}
                                              onMouseEnter={(e) => {
                                                if (!isReadOnly) {
                                                  e.currentTarget.style.transform =
                                                    "translateY(-5px)";
                                                  e.currentTarget.style.backgroundColor =
                                                    "white";
                                                  e.currentTarget.style.color =
                                                    "#000";
                                                }
                                              }}
                                              onMouseLeave={(e) => {
                                                if (!isReadOnly) {
                                                  e.currentTarget.style.transform =
                                                    "translateY(0)";
                                                  e.currentTarget.style.backgroundColor =
                                                    iconObj?.fillColor;
                                                  e.currentTarget.style.color =
                                                    value == index + 1
                                                      ? "#000"
                                                      : "#888";
                                                }
                                              }}
                                              className="nps-list"
                                              style={{
                                                backgroundColor:
                                                  iconObj?.fillColor,
                                                color:
                                                  value === index + 1
                                                    ? "#000"
                                                    : "#888",
                                                border:
                                                  value == index + 1
                                                    ? "2px solid rgb(255, 255, 255)"
                                                    : "1px solid #dee2e6",
                                                boxShadow:
                                                  value == index + 1
                                                    ? "0 4px 8px rgba(0, 0, 0, 0.2)"
                                                    : "none",
                                                transition: isReadOnly
                                                  ? "none"
                                                  : "background-color 0.3s",
                                                pointerEvents: isReadOnly
                                                  ? "none"
                                                  : "auto",
                                              }}
                                            >
                                              {iconObj?.label}
                                            </span>
                                          ),
                                        })
                                      )}
                                      iconsCount={10}
                                      disableFillHover={true}
                                    />
                                  </div>
                                  <br />
                                  {errors[
                                    `question_${question?.QuestionId}`
                                  ] && (
                                    <span className="text-danger">
                                      {getErrorMessage(question?.QuestionId)}
                                    </span>
                                  )}
                                </>
                              );
                            }}
                          />
                        )}

                        {responseDetail?.ResponseTypeName ===
                          "ThumbsUpDown" && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            render={({ field: { onChange, value } }) => (
                              <>
                                <Rating
                                  onClick={(value) => {
                                    onChange(value === 2 ? "true" : "false");
                                  }}
                                  size={30}
                                  transition
                                  readonly={!applyValidation && !isDraft}
                                  initialValue={
                                    value === "true"
                                      ? 2
                                      : value === "false"
                                      ? 1
                                      : undefined
                                  }
                                  customIcons={ThumsIcons?.map(
                                    (iconObj, index) => ({
                                      icon: (
                                        <span
                                          aria-label={`Thumbs ${index + 1}`}
                                          key={index}
                                          style={{
                                            margin: "8px",
                                            color:
                                              (value === "true" &&
                                                index === 1) ||
                                              (value === "false" && index === 0)
                                                ? iconObj?.fillColor
                                                : "#ccc",
                                            transition: "color 0.9s",
                                          }}
                                        >
                                          {iconObj?.icon}
                                        </span>
                                      ),
                                    })
                                  )}
                                  iconsCount={2}
                                  disableFillHover={true}
                                />
                                <br />
                                {errors[`question_${question?.QuestionId}`] && (
                                  <span className="text-danger">
                                    {getErrorMessage(question?.QuestionId)}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        )}

                        {(responseDetail?.ResponseTypeName ===
                          "3 Star Rating" ||
                          responseDetail?.ResponseTypeName ===
                            "5 Star Rating" ||
                          responseDetail?.ResponseTypeName ===
                            "10 Star Rating") && (
                          <Controller
                            name={`question_${question?.QuestionId}`}
                            control={control}
                            rules={{
                              required:
                                applyValidation && question?.IsRequired === true
                                  ? "This field is required"
                                  : false,
                            }}
                            render={({ field: { onChange, value } }) => {
                              const starsCount =
                                responseDetail?.ResponseTypeName ===
                                "3 Star Rating"
                                  ? 3
                                  : responseDetail?.ResponseTypeName ===
                                    "5 Star Rating"
                                  ? 5
                                  : 10;

                              return (
                                <>
                                  <Rating
                                    onClick={(value) => {
                                      onChange(
                                        responseDetail?.ResponseTypeName ===
                                          "3 Star Rating"
                                          ? (value * 6) / 10
                                          : value
                                      );
                                      handleInputChange(
                                        question?.QuestionId,
                                        value
                                      );
                                    }}
                                    size={25}
                                    transition
                                    readonly={!applyValidation && !isDraft}
                                    initialValue={parseInt(value) || 0}
                                    iconsCount={starsCount}
                                    customIcons={Array.from(
                                      { length: starsCount },
                                      (_, index) => ({
                                        icon: (
                                          <span
                                            className="star-icon"
                                            key={index}
                                            aria-label={`Star ${index + 1}`}
                                            style={{
                                              margin: "3px",
                                              color:
                                                value > index
                                                  ? "#f1c40f"
                                                  : "#ccc",
                                              transition: "color 0.9s",
                                            }}
                                          >
                                            <MdStar size={35} />
                                          </span>
                                        ),
                                      })
                                    )}
                                  />
                                  <br />
                                  {errors[
                                    `question_${question?.QuestionId}`
                                  ] && (
                                    <span className="text-danger">
                                      {getErrorMessage(question?.QuestionId)}
                                    </span>
                                  )}
                                </>
                              );
                            }}
                          />
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="px-4">
            <div className="alert alert-danger mt-3 py-2">
              No questions available
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RecursiveInnerDetailSurveyForm;
